'use client';
import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import RadioGroup from '@/components/HoseholdRadioCompoenent';
import GenderSelect from '@/components/GenderSelect';
import CurrencyDropdown from '@/components/currencyDropDown';
import StyledDatePicker from '@/components/StyledDatePicker';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { fetchIntermediaryContentByUrl } from '@/utils/intermediary_service';
import { stripLocalePrefixFromPath } from '@/utils/seo';
import { getLocaleFromPath } from '@/utils/getLocaleFromPath';
import { useTranslation } from '@/contexts/TranslationContext';
import { formatCurrency } from '@/utils/formatLocale';
import safeWindow from '@/utils/safeWindow';
import Link from '@/components/LocaleLink';
import useLocalePath from '@/hooks/useLocalePath';

const CalculateAnnualIncomePage = () => {
  const resultScrollRef = useRef(null);
  const router = useRouter();
  const localePath = useLocalePath();
  const { t, locale } = useTranslation();

  const [formData, setFormData] = useState({
    dob: '',
    property_value: 0,
    bank_cash: 0,
    retirement: 0,
    other_assets: 0,
    property_loans: 0,
    car_loans: 0,
    business_loans: 0,
    credit_debt: 0,
    gender: 'm',
  });

  const [results, setResults] = useState(null);
  const [getGender, setGetGender] = useState('individual');
  const [contentBlock, setContentBlock] = useState(null); // ✅ dynamic content block state

  // ✅ Fetch dynamic content once
  useEffect(() => {
    const fetchData = async () => {
      try {
        const pathname = safeWindow.location.pathname;
        const locale = getLocaleFromPath(pathname);
        const content = await fetchIntermediaryContentByUrl(stripLocalePrefixFromPath(pathname), locale);
        setContentBlock(content?.generator);
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchData();
  }, []);

  // Input handler for currency fields
  const handleCurrencyInput = (e) => {
    const { name, value } = e.target;
    let cleaned = value.replace(/[^\d]/g, '');
    let rawValue = Number(cleaned);
    setFormData({
      ...formData,
      [name]: rawValue,
    });
  };

  // Age calculator
  const getAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Annual Income calculation with DOB validation
  const calculateAnnualIncome = () => {
    if (!formData.dob || !dayjs(formData.dob).isValid()) {
      alert(t('calculator.validDob'));
      return;
    }

    // Income fields
    const incomeFields = [
      'salary',
      'bonus_Commissions',
      'self_Employment',
      'rental_Income',
      'investment_Income',
      'retirement_Pensions',
      'government_Benefits',
      'other_Income',
    ];

    // Deduction fields
    const deductionFields = ['total_Taxes', 'retirement_Contributions', 'insurance_other_Deductions'];

    const totalIncome = incomeFields.reduce((sum, key) => sum + (Number(formData[key]) || 0), 0);

    const totalDeductions = deductionFields.reduce((sum, key) => sum + (Number(formData[key]) || 0), 0);

    const annualNetIncome = totalIncome - totalDeductions;

    setResults({
      totalIncome,
      totalDeductions,
      annualNetIncome,
    });

    setTimeout(() => {
      resultScrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const age = getAge(formData.dob);

  const handleCompareClick = () => {
    if (!formData.dob || !dayjs(formData.dob).isValid()) {
      alert(t('calculator.validDobFirst'));
      return;
    }

    const mapGender = {
      m: 'men',
      f: 'women',
      h: 'household',
    };

    const genderKey = formData.gender.toLowerCase();
    const genderPath = getGender === 'household' ? 'household' : mapGender[genderKey];

    if (!genderPath) {
      alert(t('calculator.invalidGender'));
      return;
    }

    router.push(localePath(`/annual-income/${genderPath}/${age}-years-old`));
  };

  function getUserDescription(dob, gender, country) {
    const birthDate = new Date(dob);
    const today = new Date();

    let descAge = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      descAge--;
    }

    const g = gender?.toLowerCase();
    const normalizedGender = g === 'm' ? 'male' : g === 'f' ? 'female' : 'other';
    const finalGender = getGender === 'household' ? 'household' : normalizedGender;

    return t('calculator.userDescription', { age: descAge, gender: finalGender, country });
  }

  return (
    <>
      <div
        className="calculator-section chart-summary-wrapper scatter-chart-wrapper mt-4 mb-0"
        id={`networth-calculator-container`}
      >
        <div className="row">
          <md-card>
            <md-card-content>
              <div id={`networth-calculator_container`} className={`chart-top content-container max-width d-block p-4`}>
                {/* Header */}
                {/* {contentBlock?.title && (
                  <h1 className="title" dangerouslySetInnerHTML={{ __html: contentBlock.title }}></h1>
                )} */}
                <h1 className="title">{t('calculator.ai.title')}</h1>
                <nav aria-label={t('aria.breadcrumb')}>
                  <ol className="breadcrumb">
                    <Link className="breadcrumb-item" prefetch={false} href="/">
                      {t('nav.home')}
                    </Link>
                    <Link className="breadcrumb-item" prefetch={false} href="/calculators">
                      {t('nav.calculators')}
                    </Link>
                    <li className="breadcrumb-item active" aria-current="page">
                      {t('chartType.annualIncome')}
                    </li>
                  </ol>
                </nav>
                <div className="row">
                  {contentBlock?.top_content && (
                    <div className="col-12" dangerouslySetInnerHTML={{ __html: contentBlock.top_content }}></div>
                  )}
                </div>

                {/* <div className="d-flex justify-content-center mt-4">
                  <Image
                    priority
                    className="lines"
                    src="/assets/images/lines_White_BG.svg"
                    alt="lines"
                    height="20"
                    width="200"
                  />
                </div> */}

                {/* Form */}
                <div className="calculator-from-container mt-4">
                  <div
                    className="d-flex flex-column justify-content-center align-items-center"
                    style={{ maxWidth: '600px', width: '100%' }}
                  >
                    <div style={{ width: '100%' }}>
                      <h3>{t('calculator.profile')}</h3>
                      <p className="color-light-brown mt-1">
                        {t('calculator.ai.profileDesc')}
                      </p>
                      <RadioGroup pathname={false} getGender={setGetGender} />
                    </div>

                    <div style={{ padding: '2rem 0rem', width: '100%' }}>
                      <CurrencyDropdown
                        label={t('currency.chooseCountry')}
                        onSelect={(option) => console.log('Selected:', option)}
                      />
                    </div>

                    <GenderSelect
                      formData={formData}
                      hashArray={false}
                      onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                    />

                    <DataSelector
                      formData={formData}
                      handleCurrencyInput={handleCurrencyInput}
                      params={{ unit: 'USD' }}
                      setFormData={setFormData}
                    />

                    <button className="btn btn-primary viewChartChange mt-5" onClick={calculateAnnualIncome}>
                      {t('calculator.ai.calculate')}
                    </button>
                  </div>
                </div>
              </div>
            </md-card-content>
          </md-card>
        </div>
      </div>

      {/* Results */}
      {results && (
        <section
          className={`results-section py-4 chart-summary-wrapper scatter-chart-wrapper mt-0 mb-4`}
          style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}
        >
          <div className="divder-green mt-4 w-100">
            <div class="eclispe chart-eclipse-right charteclipse-top"></div>
            <div class="eclispe chart-eclipse-left chart-eclipse-top"></div>
          </div>
          <div className="row text-center" style={{ maxWidth: 600, width: '100%' }}>
            {/* <div class="eclispe chart-eclipse-right charteclipse-top"></div>
            <div class="eclispe chart-eclipse-left chart-eclipse-top"></div> */}
            {results && (
              <div className="results-wrapper mt-5" ref={resultScrollRef}>
                <div className="results-header">
                  <h3>{t('baseChart.yourResults')}</h3>
                </div>

                <div className="results-container">
                  <div className="result-item">
                    <span className="label">{t('calculator.ageGender')}</span>
                    <span className="value">{getUserDescription(formData.dob, formData.gender, 'USA')}</span>
                  </div>

                  <div className="result-item">
                    <span className="label">{t('calculator.ai.totalGrossIncome')}</span>
                    <span className="value">{formatCurrency(results.totalIncome, locale)}</span>
                  </div>

                  <div className="result-item">
                    <span className="label">{t('calculator.ai.totalDeductions')}</span>
                    <span className="value">{formatCurrency(results.totalDeductions, locale)}</span>
                  </div>

                  <div className="result-item">
                    <span className="label">{t('calculator.method')}</span>
                    <span className="value">{t('calculator.ai.methodDesc')}</span>
                  </div>

                  <div className="result-item" style={{ fontWeight: 600 }}>
                    <span className="label" style={{ fontWeight: 600 }}>
                      {t('calculator.ai.resultLabel')}
                    </span>
                    <span className="value net-worth-value">{formatCurrency(results.annualNetIncome, locale)}</span>
                  </div>
                </div>

                {age >= 15 && age <= 100 && (
                  <div className="d-flex justify-content-center mt-4">
                    <button
                      className="btn btn-primary viewChartChangeYello"
                      style={{ background: 'linear-gradient(119.6deg, #FFAB00 25.48%, #D08B00 55.77%)' }}
                      onClick={handleCompareClick}
                    >
                      {t('calculator.compareWithOthers')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}
      <div className="row">
        {contentBlock?.content && (
          <div className="col-12" dangerouslySetInnerHTML={{ __html: contentBlock.content }}></div>
        )}
      </div>
    </>
  );
};

export default CalculateAnnualIncomePage;
const DataSelector = ({ formData, handleCurrencyInput, params, setFormData }) => {
  const { t } = useTranslation();

  const renderCurrencyInput = (field) => (
    <div className="input-field-container px-0 mt-4" key={field.name}>
      <div className="input-field-label">
        <span>{field.label}</span>
      </div>
      <div className="col">
        <div className="input-wrapper">
          <input
            name={field.name}
            type="text"
            inputmode="numeric"
            value={formData[field.name] ? Number(formData[field.name]).toLocaleString() : ''}
            className="dob text-dark form-control bg-content-orange dop-input"
            onChange={handleCurrencyInput}
            placeholder={field.placeholder}
          />
          <span className="text-content-orange mr-2" style={{ marginRight: '6px' }}>
            {params.unit || 'USD'}
          </span>
        </div>
        {field.hint && <span className="color-light-brown mt-1">{field.hint}</span>}
      </div>
    </div>
  );

  const handleDateChange = (newValue) => {
    setFormData({
      ...formData,
      dob: newValue ? newValue.format('YYYY-MM-DD') : '',
    });
  };

  return (
    <div className="cal-selector-container mt-4">
      {/* DOB */}
      <div className="input-field-container px-0">
        <div className="sub-title-chart mb-3">
          <span>{t('form.dateOfBirth')}</span>
        </div>
        <div className="col">
          <div className="input-wrapper">
            <StyledDatePicker
              name="dob"
              id="dob"
              className="dob text-dark form-control bg-content-orange dop-input"
              value={formData.dob ? dayjs(formData.dob) : null}
              onChange={handleDateChange}
              iconTop={'50%'}
              minHeight={'100%'}
              padding={'0px'}
              iconColor={'#7C5832'}
            />
          </div>
          <span className="color-light-brown mt-1">{t('baseChart.dateHint')}</span>
        </div>
      </div>

      <div className="d-flex justify-content-center mt-4">
        <Image priority className="lines" src="/assets/images/lines_White_BG.svg" alt="lines" height="20" width="200" />
      </div>

      <h4 className="form-subtitle">{t('calculator.ai.incomeHeading')}</h4>
      <p className="color-light-brown mt-1" dangerouslySetInnerHTML={{ __html: t('calculator.ai.incomeDesc') }} />
      {[
        {
          name: 'salary',
          label: t('calculator.ai.salary'),
          hint: t('calculator.ai.salaryHint'),
          placeholder: '500,000',
        },
        {
          name: 'bonus_Commissions',
          label: t('calculator.ai.bonus'),
          hint: t('calculator.ai.bonusHint'),
          placeholder: '5,000',
        },
        {
          name: 'self_Employment',
          label: t('calculator.ai.selfEmployment'),
          hint: t('calculator.ai.selfEmploymentHint'),
          placeholder: '8,000',
        },
        {
          name: 'rental_Income',
          label: t('calculator.ai.rentalIncome'),
          hint: t('calculator.ai.rentalIncomeHint'),
          placeholder: '10,000',
        },
        {
          name: 'investment_Income',
          label: t('calculator.ai.investmentIncome'),
          hint: t('calculator.ai.investmentIncomeHint'),
          placeholder: '3,000',
        },
        {
          name: 'retirement_Pensions',
          label: t('calculator.ai.retirementPensions'),
          hint: t('calculator.ai.retirementPensionsHint'),
          placeholder: '0',
        },
        {
          name: 'government_Benefits',
          label: t('calculator.ai.governmentBenefits'),
          hint: t('calculator.ai.governmentBenefitsHint'),
          placeholder: '0',
        },
        {
          name: 'other_Income',
          label: t('calculator.ai.otherIncome'),
          hint: t('calculator.ai.otherIncomeHint'),
          placeholder: '0',
        },
      ].map(renderCurrencyInput)}

      <div className="d-flex justify-content-center mt-4">
        <Image priority className="lines" src="/assets/images/lines_White_BG.svg" alt="lines" height="20" width="200" />
      </div>

      <h4 className="form-subtitle">{t('calculator.ai.deductionsHeading')}</h4>
      <p className="color-light-brown mt-1" dangerouslySetInnerHTML={{ __html: t('calculator.ai.deductionsDesc') }} />
      {[
        {
          name: 'total_Taxes',
          label: t('calculator.ai.totalTaxes'),
          hint: t('calculator.ai.totalTaxesHint'),
          placeholder: '15,000',
        },
        {
          name: 'retirement_Contributions',
          label: t('calculator.ai.retirementContributions'),
          hint: t('calculator.ai.retirementContributionsHint'),
          placeholder: '5,000',
        },
        {
          name: 'insurance_other_Deductions',
          label: t('calculator.ai.insuranceDeductions'),
          hint: t('calculator.ai.insuranceDeductionsHint'),
          placeholder: '1,000',
        },
      ].map(renderCurrencyInput)}
    </div>
  );
};
